import { Store } from "@ngrx/store";
import { BehaviorSubject, Subscription } from "rxjs";
import { Router } from "@angular/router";
import { Injector, Injectable, inject } from "@angular/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { HttpService } from "../http-service/http-client.service";
import { NAVIGATION } from "src/app/store/actions/navigation.action";
import { FirebaseService } from "../firebase-service/firebase.service";
import { NavigationModel } from "src/app/store/models/navigation.model";
import { NotificationLoggerModel } from "src/app/store/models/notification.model";
import { SecurityMappingCheckinService } from "../settings-service/security-mapping/security-mapping-checkin.service";

@Injectable({ providedIn: "root" })
export class NotificationService {
  private store = inject(Store);
  private router = inject(Router);
  private injector = inject(Injector);
  httpService = inject(HttpService);
  firebaseService = inject(FirebaseService);

  isErrorModalOpen = new BehaviorSubject(false);
  public fcmToken = new BehaviorSubject<string>("");
  isUserListModalOpen = new BehaviorSubject(false);
  public deviceToken = new BehaviorSubject<string>("");
  public errorAssetsCount = new BehaviorSubject<number>(0);
  public sideMenuBar = new BehaviorSubject<boolean>(false);
  public isInitialized = new BehaviorSubject<boolean>(false);
  public isFromSetting = new BehaviorSubject<boolean>(false);
  public newNotificationCountSubject = new BehaviorSubject<number>(0);
  public isCheckedInFromSetting = new BehaviorSubject<boolean>(false);
  public notifications = new BehaviorSubject<NotificationLoggerModel>({});
  public allNotifications = new BehaviorSubject<NotificationLoggerModel[]>([]);
  newNotificationCount$ = this.newNotificationCountSubject.asObservable();
  private countSubscription: Subscription | null = null;
  constructor() {}

  initializeNotification() {
    this.RegisterNotifications();
    this.NotificationListeners();
  }

  private RegisterNotifications = async () => {
    let permission = await PushNotifications.checkPermissions();

    if (permission.receive === "prompt") {
      permission = await PushNotifications.requestPermissions();
    }

    if (permission.receive !== "granted") {
      await PushNotifications.requestPermissions();
    }

    await PushNotifications.register();
  };

  private NotificationListeners = async () => {
    await PushNotifications.addListener("registration", (token) => {
      this.fcmToken.next(token.value);
      this.deviceToken.next(token.value);
      this.isInitialized.next(true);
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.error("Registration error:", err.error);
    });

    await PushNotifications.addListener(
      "pushNotificationReceived",
      (notification) => {
        const Notifications = {
          body: notification.body!,
          title: notification.title!,
        };

        this.notifications.next(Notifications);
      },
    );

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification) => {
        if (notification.actionId === "tap") {
          const data = notification.notification.data;
          // Security mapping expiry reminder (developer's request 2026-10-08); the
          // service is looked up on tap, so it isn't created at app start
          if (data?.module === "security_mapping") {
            this.sideMenuBar.next(false);
            void this.injector
              .get(SecurityMappingCheckinService)
              .OpenReminderItem(data);
            return;
          }
          const isAssetApproval = data?.module === "asset-approval";

          const navigation: NavigationModel = isAssetApproval
            ? {
                assetApproval: {
                  type: data?.type,
                  module: data?.module,
                  siteId: data?.siteId,
                  assetId: data?.assetId,
                  route: `settings/structural/${data?.siteId}`,
                },
              }
            : {
                assetRegister: {
                  type: data?.type!,
                  module: data?.module!,
                  siteId: data?.siteId!,
                  assetId: data?.assetId!,
                  route: `settings/structural/${data?.siteId}`,
                },
              };

          this.store.dispatch(NAVIGATION(navigation));
          this.router.navigate(["settings"]);
          this.sideMenuBar.next(false);
        }
      },
    );
  };

  public async GetAllNotifications(
    email: string,
    lastIndex: number,
    limit: number = 10,
  ): Promise<{ notifications: NotificationLoggerModel[]; lastIndex: number }> {
    return new Promise((resolve, reject) => {
      this.firebaseService.getNotifications(email, lastIndex, limit).subscribe({
        next: (data) => {
          resolve({
            notifications: data.notifications,
            lastIndex: data.lastIndex || 0, // Set to 0 if no more data
          });
        },
        error: (err) => {
          console.error("Error fetching notifications:", err);
          reject(err);
        },
      });
    });
  }

  // public GetAllNotifications = async (
  //   email: string,
  //   lastIndex: number,
  //   limit: number = 10,
  // ) => {
  //   return new Promise((resolve, reject) => {
  //     this.firebaseService.getNotifications(email, lastIndex, limit).subscribe({
  //       next: (data) => {
  //         if (data.notifications) {
  //           resolve({
  //             notifications: data.notifications,
  //             lastIndex: data.lastIndex,
  //           });
  //         } else {
  //           resolve({ notifications: [], lastIndex: lastIndex });
  //         }
  //       },
  //       error: (err) => {
  //         console.error("Error fetching notifications:", err);
  //         reject(err);
  //       },
  //     });
  //   });
  // };

  public UpdateNotification = async (email: string, data: any) => {
    this.firebaseService.updateNotification(email, data).subscribe({
      error: (err) => {
        console.error("Error fetching notifications:", err);
      },
    });
  };

  public async GetNewNotificationCount(email: string) {
    if (this.countSubscription) {
      this.countSubscription.unsubscribe();
    }
    this.countSubscription = this.firebaseService.getLiveNotificationCount(email).subscribe((count) => {
      this.newNotificationCountSubject.next(count);
    });
  }
}
